/**
 * i18n layer — /i18n/tr.json + /i18n/en.json (UI chrome only).
 * Content fields ({tr,en}) and user prose are NEVER auto-translated (A-05).
 */
import { useCallback } from 'react';
import en from '@i18n/en.json';
import tr from '@i18n/tr.json';
import type { Locale, LocalizedString } from '@/domain/shared';
import { useAppState } from '@/state/store';

type Dict = Record<string, unknown>;

function flatten(obj: Dict, prefix: string, out: Record<string, string>): Record<string, string> {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      flatten(v as Dict, key, out);
    } else if (typeof v === 'string') {
      out[key] = v;
    }
  }
  return out;
}

const flat: Record<Locale, Record<string, string>> = {
  en: flatten(en as unknown as Dict, '', {}),
  tr: flatten(tr as unknown as Dict, '', {}),
};

export interface Translate {
  (key: string, params?: Record<string, string | number>): string;
}

/** Missing keys fall back to English, then to the key itself (visible). */
export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  let text = flat[locale][key] ?? flat.en[key] ?? key;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replaceAll(`{${name}}`, String(value));
    }
  }
  return text;
}

export function useT(): Translate {
  const { ui } = useAppState();
  return useCallback(
    (key, params) => translate(ui.locale, key, params),
    [ui.locale],
  );
}

/**
 * Display value for a {tr,en} field with locale fallback (reading only).
 * Editing always writes to the *current* locale field — never auto-translates.
 */
export function localized(value: LocalizedString, locale: Locale): string {
  return (locale === 'tr' ? (value.tr ?? value.en) : (value.en ?? value.tr)) ?? '';
}
